/**
 * The question a create-link form must ask, in scan terms, with the answer pre-selected.
 *
 * `Decisions/2026-08-04 The Default Slot` closes with a **UI obligation**, and it is the
 * only ruling in this project whose deliverable is a sentence rather than a behaviour:
 *
 * > *Creating a link at a finer grain always asks, in scan terms, where plain scans of that
 * > grain should land.* Type matches the default it would shadow → pre-select **"make this
 * > LOT42's default"**. Type does not match → pre-select **"leave the default unchanged"**.
 * > *Consequence stated either way: "Plain scans of LOT42 will land here" / "…will keep
 * > landing on the Standard product page."* **Silence is wrong under every reading.**
 *
 * ## Why this is a function and not JSX
 *
 * Same reason as `link-row.ts`, and this one is the sharper case: the question lives inside
 * a form, the form lives behind a button, and c34 measured that `renderToStaticMarkup`
 * attaches no reconciler — so **nothing in this repository can click it.** A rule left in
 * the JSX would be tested by a hand-copied reconstruction of the JSX, which stays green
 * while the real screen changes. Exported first, deliberately, before its test exists.
 *
 * ## The generalisation is the reason the ruling exists, and it is easy to lose
 *
 * §3.8 warns that a lot-level recall link alone is invisible to a camera scan. The rule is
 * broader: **any finer-grained link intended to change where plain scans land must occupy
 * its grain's default slot.** §3.8 enforces it for recalls; nothing enforces it for a brand
 * creating a lot-level advisory — same failure shape, no warning, the link exists, the
 * dashboard shows it, and scans never reach it. This question *is* the enforcement, and the
 * API's `DEFAULT_SLOT_UNCLAIMED` warning is the machine-client half of the same guard.
 *
 * ## What "type matches" means, precisely
 *
 * The new link's type is the same as the type of the link that currently holds the coarser
 * grain's default slot. That is the case where a naive user's prediction — *"I made a page
 * for LOT42, so LOT42 scans go there"* — comes true **through** the explicit flag rather
 * than despite it. A non-matching type (an allergen sheet, an SDS, a promo) is a link
 * nobody expects to move plain scans, so moving them would be the surprise.
 */
import type { LinkSummary } from "@packagepage/shared";

/** The type currently holding the default slot a new link would shadow, if any. */
export interface DefaultSlotContext {
  /**
   * The grain being created at, rendered as a Digital Link path — `/01/G/10/LOT42`.
   *
   * A path rather than a lot value, because the ruling's own copy is in **scan terms** and
   * a path is what a scan is. Built by `digitalLinkPath` in `packages/shared`, so it is the
   * same rendering the API's refusal copy uses.
   */
  readonly scanPath: string;
  /** The link type the new link declares. */
  readonly linkType: string;
  /**
   * The link type holding the default slot that a plain scan of this grain reaches today,
   * or null when nothing does.
   *
   * From the merge preview's `selected` — the real merge — rather than guessed at from the
   * rows, for the same reason the simulator is backed by the endpoint. At the GTIN grain
   * there is no inheritance to merge, so the caller passes the slot's own occupant row.
   */
  readonly incumbentLinkType: string | null;
  /** Where a plain scan of this grain lands today, for the consequence sentence. */
  readonly incumbentTitle: string | null;
  /**
   * Whether the type may hold a default at all.
   *
   * False for the three types GS1 restricts to the resolver. The API refuses those with
   * `LINK_TYPE_NOT_ASSIGNABLE`; offering the question and then relaying that refusal would
   * be asking a user to choose something the server was always going to decline.
   */
  readonly isDefaultEligible: boolean;
  /** Why the GTIN slot cannot change on this product, from {@link gtinDefaultLock}. */
  readonly gtinLock?: string | null;
}

/**
 * Why the GTIN grain's default cannot move on a product in `status`, or null on a draft.
 *
 * Unknown status (preview still loading) returns null; the API refuses the act anyway.
 */
export function gtinDefaultLock(
  status: string | null,
  scanPath: string,
  occupantTitle: string | null,
): string | null {
  if (status === null || status === "draft") return null;
  // `build.ts` tombstones only this status, emitting `links: []` — there is no default to move.
  if (status === "tombstoned") {
    return (
      `This product is tombstoned. Its code answers every scan with a tombstone and no ` +
      `link, so there is no default to move.`
    );
  }
  // Published and archived entries are live and must hold a GTIN default — ops/links.ts:84-89.
  return (
    `${occupantTitle ?? "The current default"} answers plain scans of ${scanPath}. A ` +
    `${status} product must always have a link doing that, so it cannot be cleared or ` +
    `replaced here.`
  );
}

export interface DefaultSlotQuestion {
  /** Whether to ask. False for a type that cannot hold a default, or a locked GTIN slot. */
  // Asked at the GTIN grain too: until U23 it was not, the form always sent `false`, and
  // publish refused every dashboard-made product with `PUBLISH_NEEDS_DEFAULT` — Drift Log U23.
  readonly ask: boolean;
  /** The ruling's pre-selection. `true` = "make this the default for this grain". */
  readonly preselected: boolean;
  /** Why it is pre-selected that way, in one line, so the choice is not a mystery. */
  readonly reason: string;
  /** The consequence of the *selected* answer. Rendered whether or not the user changes it. */
  readonly consequenceIfDefault: string;
  readonly consequenceIfNot: string;
}

/**
 * The question, or `ask: false`.
 *
 * `consequenceIfDefault` and `consequenceIfNot` are **both** returned rather than one
 * "current consequence", so the screen can show the other one beside the checkbox. The
 * ruling's requirement is that the consequence is stated *either way* — a form that only
 * described the state it was currently in would make the user toggle a safety-relevant flag
 * to find out what it does.
 */
export function defaultSlotQuestion(
  ctx: DefaultSlotContext & { readonly isGtinGrain: boolean },
): DefaultSlotQuestion {
  const landsHere = `Plain scans of ${ctx.scanPath} will land here.`;
  const landsThere =
    ctx.incumbentTitle === null
      ? `Plain scans of ${ctx.scanPath} will keep landing wherever they land today, not here.`
      : `Plain scans of ${ctx.scanPath} will keep landing on ${ctx.incumbentTitle}.`;

  if (!ctx.isDefaultEligible) {
    return {
      ask: false,
      preselected: false,
      reason: `${ctx.linkType} is reserved for the resolver and cannot hold a default slot.`,
      consequenceIfDefault: landsHere,
      consequenceIfNot: landsThere,
    };
  }

  // At the GTIN grain the incumbent is the slot's own occupant, not a coarser link, so the
  // question is whether this link answers plain scans — and publishing needs one that does.
  if (ctx.isGtinGrain) {
    const empty = ctx.incumbentLinkType === null;
    if (ctx.gtinLock != null) {
      return {
        ask: false,
        preselected: false,
        reason: ctx.gtinLock,
        consequenceIfDefault: landsHere,
        consequenceIfNot: landsThere,
      };
    }
    return {
      ask: true,
      preselected: empty,
      reason: empty
        ? `Nothing answers plain scans of ${ctx.scanPath} yet. This product cannot be ` +
          `published until one link does, so tick this unless another link will.`
        : `${ctx.incumbentTitle ?? "Another link"} answers plain scans of ` +
          `${ctx.scanPath} now. Ticking this moves it out of that slot — it stays ` +
          `available to anything that asks for ${ctx.incumbentLinkType} by name.`,
      consequenceIfDefault: landsHere,
      consequenceIfNot: empty
        ? `Plain scans of ${ctx.scanPath} will still have no destination, and publishing ` +
          `will be refused until a link holds this slot.`
        : landsThere,
    };
  }

  // The ruling's pre-selection, and it turns on one comparison.
  const typeMatches =
    ctx.incumbentLinkType !== null && ctx.incumbentLinkType === ctx.linkType;

  return {
    ask: true,
    preselected: typeMatches,
    reason: typeMatches
      ? `A ${ctx.linkType} link here shadows the ${ctx.linkType} that plain scans reach ` +
        `today, so this is almost certainly what you mean.`
      : `Shadowing is per link type. A ${ctx.linkType} link never moves plain scans by ` +
        `existing — it has to hold this grain's default slot, and most links of this kind ` +
        `should not.`,
    consequenceIfDefault: landsHere,
    consequenceIfNot: landsThere,
  };
}

/** The slot a link claims: its grain, language list and media type. */
export interface DefaultSlotKey {
  readonly grain: {
    readonly cpv: string | null;
    readonly lot: string | null;
    readonly serial: string | null;
  };
  readonly hreflang: readonly string[] | null;
  readonly mediaType: string | null;
}

/**
 * The link already holding `slot`, other than `excludeId`, or null.
 *
 * Same key as the API's `findDefaultOccupant` — `hreflang` compares in order, as `text[]`
 * equality does — so the swap below names the row the server would refuse over.
 */
export function defaultSlotOccupant(
  links: readonly LinkSummary[],
  slot: DefaultSlotKey,
  excludeId: string | null,
): LinkSummary | null {
  const langs = (l: readonly string[] | null) => (l ?? []).join("\u0000");
  return (
    links.find(
      (l) =>
        l.isDefault &&
        l.id !== excludeId &&
        (l.grain.cpv ?? null) === slot.grain.cpv &&
        (l.grain.lot ?? null) === slot.grain.lot &&
        (l.grain.serial ?? null) === slot.grain.serial &&
        langs(l.hreflang) === langs(slot.hreflang) &&
        (l.mediaType ?? null) === slot.mediaType,
    ) ?? null
  );
}
